/**
 * Product "contenance" parsing (catalogue revision module, P6).
 *
 * French pharmacy catalogues describe a product's size and galenic form in a
 * dedicated field. Cocooncenter publishes it verbatim as `Contenance`
 * ("30 comprimés", "45 gélules", "3 x 30 gélules", "32,5 ml"); other sources only
 * bury it in the name or the URL slug. This module turns all of those into one
 * structured shape so the internal revision screen can propose a correction.
 *
 * Deliberately SEPARATE from `extractSize` in `names.ts`:
 *  - `extractSize` serves the `match_v1` scorer, whose canonical units are only
 *    `ml` and `g`; it must not change without an `algo_version` bump and a
 *    golden re-run (docs/AGENTS rule 5).
 *  - Here we need the *units the catalogue actually uses* — comprimés, gélules,
 *    sachets — which `extractSize` deliberately throws away.
 *
 * Version tag: `contenance_v1`. Pure, deterministic, unit-tested.
 */
/** Volume / mass → canonical unit, with the conversion factor. */
const MEASURE_UNITS = {
    ml: { unit: 'ml', factor: 1 },
    mls: { unit: 'ml', factor: 1 },
    millilitre: { unit: 'ml', factor: 1 },
    millilitres: { unit: 'ml', factor: 1 },
    centilitre: { unit: 'ml', factor: 10 },
    centilitres: { unit: 'ml', factor: 10 },
    cl: { unit: 'ml', factor: 10 },
    l: { unit: 'ml', factor: 1000 },
    litre: { unit: 'ml', factor: 1000 },
    litres: { unit: 'ml', factor: 1000 },
    g: { unit: 'g', factor: 1 },
    gr: { unit: 'g', factor: 1 },
    gramme: { unit: 'g', factor: 1 },
    grammes: { unit: 'g', factor: 1 },
    kg: { unit: 'g', factor: 1000 },
    kilo: { unit: 'g', factor: 1000 },
    kilos: { unit: 'g', factor: 1000 },
    mg: { unit: 'g', factor: 0.001 },
};
/**
 * Countable units → canonical unit. Only *forms of the product itself* belong
 * here; "boîte" and "lot" are packaging, handled separately, because a boîte
 * is not what the patient ingests.
 */
const COUNT_UNITS = {
    comprime: 'comprime',
    comprimes: 'comprime',
    comprimee: 'comprime',
    comprimees: 'comprime',
    cp: 'comprime',
    gelule: 'gelule',
    gelules: 'gelule',
    gel: 'gelule',
    capsule: 'capsule',
    capsules: 'capsule',
    sachet: 'sachet',
    sachets: 'sachet',
    dose: 'sachet',
    doses: 'sachet',
    ampoule: 'ampoule',
    ampoules: 'ampoule',
    piece: 'piece',
    pieces: 'piece',
    unite: 'piece',
    unites: 'piece',
    pastille: 'piece',
    pastilles: 'piece',
    comprime_effervescent: 'comprime',
    comprimes_effervescents: 'comprime',
    comprime_a_croquer: 'comprime',
    comprimes_a_croquer: 'comprime',
    comprim_e: 'comprime',
    gelule_molle: 'gelule',
};
/**
 * Galenic forms. Kept separate from the countable unit because a site may state
 * "Forme: Gélule" while "Contenance" says "3 x 30 gélules" — the form is the
 * product family, the unit is what a pack holds.
 */
const FORMS = {
    comprime: 'Comprimé',
    comprimes: 'Comprimé',
    gelule: 'Gélule',
    gelules: 'Gélule',
    capsule: 'Capsule',
    capsules: 'Capsule',
    sachet: 'Sachet',
    sachets: 'Sachet',
    sirop: 'Sirop',
    solution: 'Solution',
    spray: 'Spray',
    pommade: 'Pommade',
    creme: 'Crème',
    gel: 'Gel',
    huile: 'Huile',
    suppositoire: 'Suppositoire',
    ampoule: 'Ampoule',
    ampoules: 'Ampoule',
    patch: 'Patch',
    dispositif: 'Dispositif',
};
/** Packaging ("Présentation" on Cocooncenter). Never a contenance unit. */
const PRESENTATIONS = {
    boite: 'Boîte',
    boites: 'Boîte',
    boite_de: 'Boîte',
    pot: 'Pot',
    pots: 'Pot',
    flacon: 'Flacon',
    flacons: 'Flacon',
    tube: 'Tube',
    tubes: 'Tube',
    lot: 'Lot',
    lots: 'Lot',
    sachet: 'Sachet',
    sachets: 'Sachet',
    blister: 'Blister',
    blisters: 'Blister',
    plaquette: 'Plaquette',
    plaquettes: 'Plaquette',
    spray: 'Spray',
    kit: 'Kit',
    kits: 'Kit',
    coffret: 'Coffret',
    coffrets: 'Coffret',
    rollon: 'Roll-on',
    spray_double: 'Spray double',
};
function deaccent(input) {
    return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
/** Lowercase, de-accented, punctuation-flattened key for lexicon lookups. */
function key(input) {
    return deaccent(input.toLowerCase())
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
/** Resolve a `Forme:` / `Présentation:` label to its canonical wording. */
export function parseForm(raw) {
    if (raw === null || raw === undefined)
        return null;
    const k = key(raw);
    if (k === '')
        return null;
    const found = FORMS[k];
    if (found !== undefined)
        return found;
    // Multi-word labels like "Comprimé à croquer" → fall back to the first word.
    const first = (k.split('_')[0] ?? '');
    return FORMS[first] ?? null;
}
export function parsePresentation(raw) {
    if (raw === null || raw === undefined)
        return null;
    const k = key(raw);
    if (k === '')
        return null;
    const found = PRESENTATIONS[k];
    if (found !== undefined)
        return found;
    const first = (k.split('_')[0] ?? '');
    return PRESENTATIONS[first] ?? null;
}
const UNIT_TOKEN = '[a-zà-ÿ]+';
/**
 * Parse a `Contenance`-style value.
 *
 * Handles, all verified against the live Cocooncenter catalogue:
 *   "30 comprimés"   → 30 comprimés, pack 1
 *   "45 gélules"     → 45 gélules,   pack 1
 *   "3 x 30 gélules" → 30 gélules,   pack 3
 *   "32,5 ml"        → 32,5 ml,      pack 1
 *   "lot de 3 x 30 gélules" → 30 gélules, pack 3
 *   "2 sachets de 5 ml"      → 5 ml, pack 2
 *
 * Returns `null` when nothing parseable is present — never a guess, because a
 * wrong contenance is worse than a missing one on a live product page.
 */
export function parseContenance(raw) {
    if (raw === null || raw === undefined)
        return null;
    const text = raw.replace(/[\u00a0\u202f]/g, ' ').trim();
    if (text === '')
        return null;
    // Normalised haystack for lexicon lookups, but keep the original for numbers.
    const flat = deaccent(text.toLowerCase()).replace(/[,.;]/g, '.');
    // ---- 1. pack multiplier -------------------------------------------------
    let packCount = 1;
    let rest = flat;
    const lot = /\blot\s+de\s+(\d{1,3})\b/.exec(flat);
    if (lot !== null) {
        packCount = Number(lot[1]);
        rest = flat.replace(lot[0], ' ');
    }
    const times = /(\d{1,3})\s*[xX]\s*(?=\d)/.exec(rest);
    if (times !== null) {
        packCount = packCount * Number(times[1]);
        rest = rest.replace(times[0], ' ');
    }
    const packOf = /(\d{1,3})\s*(?:sachets?|boites?|flacons?|ampoules?)\s+de\s+(?=\d)/.exec(rest);
    if (packOf !== null) {
        packCount = packCount * Number(packOf[1]);
        rest = rest.replace(packOf[0], ' ');
    }
    if (!Number.isFinite(packCount) || packCount < 1)
        packCount = 1;
    // ---- 2. the measure itself ---------------------------------------------
    const re = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${UNIT_TOKEN})`, 'i');
    const m = re.exec(rest);
    if (m === null)
        return null;
    const rawValue = m[1];
    const rawUnit = m[2];
    if (rawValue === undefined || rawUnit === undefined)
        return null;
    const value = Number(rawValue.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0)
        return null;
    // The regex happily matches the digits of "-5 ml"; a negative quantity is not
    // a contenance, so reject it explicitly rather than storing |value|.
    const before = rest.slice(0, m.index);
    if (/-\s*$/.test(before))
        return null;
    const uk = key(rawUnit);
    const measure = MEASURE_UNITS[uk];
    const count = COUNT_UNITS[uk];
    // Strip trailing "x" style leftovers and any second unit word.
    const tail = rest.slice(m.index + m[0].length).trim();
    const tailKey = key(tail.split(/\s+/)[0] ?? '');
    // "20 comprimés effervescents" — the head noun is the unit.
    let unit;
    if (measure !== undefined) {
        unit = measure.unit;
    }
    else if (count !== undefined) {
        unit = count;
    }
    else if (PRESENTATIONS[uk] !== undefined) {
        // A bare packaging word with a number ("30 boîtes") means 30 units.
        unit = 'piece';
    }
    else {
        return null;
    }
    // "5 ml x 10 ampoules" → the countable form wins when both are present.
    if (measure !== undefined && COUNT_UNITS[tailKey] !== undefined) {
        const tailCount = COUNT_UNITS[tailKey];
        return {
            value: value * measure.factor,
            unit: measure.unit === 'ml' ? 'ml_ampoule' : 'piece_ml',
            packCount,
            form: FORMS[tailCount] ?? null,
            presentation: null,
            raw: text,
        };
    }
    const scaled = measure === undefined ? value : value * measure.factor;
    return {
        value: round3(scaled),
        unit,
        packCount,
        form: FORMS[uk] ?? (COUNT_UNITS[uk] === undefined ? null : (FORMS[uk] ?? null)),
        presentation: PRESENTATIONS[uk] ?? null,
        raw: text,
    };
}
function round3(value) {
    return Math.round(value * 1000) / 1000;
}
/**
 * Pull the contenance out of a free-text product name when the source has no
 * dedicated field (Drakkars publishes "…-60-comprimes" only in the URL slug and
 * "Homeogene 9" as its name).
 *
 * Slugs are separated by `-`, so a hyphenated name is turned back into spaces
 * before matching — otherwise "60-comprimes" never matches the unit lexicon.
 */
export function parseContenanceFromText(text) {
    if (text === null || text === undefined)
        return null;
    const spaced = text.replace(/[-_/]+/g, ' ');
    return parseContenance(spaced);
}
/**
 * Human-readable rendering, used by the revision screen and by the proposed
 * corrected name ("30 comprimés", "3 x 30 gélules", "32,500 ml").
 */
export function formatContenance(c) {
    const unitLabels = {
        ml: 'ml',
        g: 'g',
        comprime: 'comprimés',
        gelule: 'gélules',
        capsule: 'capsules',
        sachet: 'sachets',
        ampoule: 'ampoules',
        piece: 'pièces',
        ml_ampoule: 'ml',
        piece_ml: 'pièces',
    };
    const value = Number.isInteger(c.value) ? String(c.value) : c.value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    const unit = unitLabels[c.unit];
    return c.packCount > 1 ? `${c.packCount} x ${value} ${unit}` : `${value} ${unit}`;
}
/** True when two contenances denote the same product size (unit-aware). */
export function sameContenance(a, b) {
    if (a === null || b === null)
        return a === b;
    return (Math.abs(a.value - b.value) < 0.001 &&
        a.unit === b.unit &&
        a.packCount === b.packCount);
}
//# sourceMappingURL=contenance.js.map